import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertTriangle, Calendar, ChevronDown, ChevronRight, ChevronUp, CreditCard, Clock, Bot, Landmark, Mail, ShieldAlert, FileText } from 'lucide-react';
import useAgentApprovalDecision from './useAgentApprovalDecision';
import ActionPreview from '../../Banking/reconciliation/ActionPreview';
import ReconciliationQuestion from '../../Banking/reconciliation/ReconciliationQuestion';
import {ReconciliationTranscriptLink} from '../../Banking/reconciliation/ReconciliationWorkStatus';
import { useTodos } from './TodoContext';

/**
 * TodoGroup — one card per tile. Switches on `kind`:
 *   count         — big number
 *   date          — due-date pill with days-until
 *   approval_list — itemised agent (AI) approvals with Godkänn/Avvisa actions
 *
 * Icon chip matches the employee list on /employees (rounded square with
 * `var(--rgba-primary-1)` background + `var(--bs-primary)` foreground) so
 * the ToDo feels native to the rest of the app.
 */
const DOMAIN_ICON = {
  expenses:      CreditCard,
  time_reports:  Clock,
  tax_reporting: Calendar,
  agent:         Bot,
  banking:       Landmark,
  payments:      FileText,
};

// Severity drives only the number / date text color and the action label
// color — no left ribbon, no card tinting.
const SEVERITY_ACCENT = {
  info:   '#1e40af',
  warn:   '#b45309',
  urgent: '#991b1b',
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso + 'T00:00:00');
    return new Intl.DateTimeFormat('sv-SE', { day: 'numeric', month: 'short', year: 'numeric' }).format(d);
  } catch { return iso; }
};

// Tool → human label. Unknown tools fall back to the raw name (still informative).
const TOOL_LABEL = {
  run_payroll:              'Lås lönekörning',
  publish_customer_invoice: 'Publicera kundfaktura',
  create_supplier_invoice:  'Bokför leverantörsfaktura',
  create_verification:      'Bokför verifikat',
  file_vat:                 'Lämna in momsdeklaration',
  send_reminder:            'Skicka påminnelse',
  send_customer_message:    'Skicka kundmeddelande',
  execute_reconciliation_action: 'Stäm av bankhändelse',
};

/** Is this resolved plan a rendered outbound message (to/subject/body)? */
const isRenderedMessage = (plan) =>
  plan && typeof plan.to === 'string' && typeof plan.subject === 'string' && typeof plan.body === 'string';

/** The rendered message a human signs off on — shown verbatim, never editable. */
const RenderedMessagePreview = ({ plan }) => (
  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '10px 12px', marginTop: 8 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 6 }}>
      <Mail size={12} /> MEDDELANDE SOM SKICKAS (exakt så här)
    </div>
    <div style={{ fontSize: 12, color: '#334155' }}>
      <div><span style={{ fontWeight: 600 }}>Till:</span> {plan.to}</div>
      <div><span style={{ fontWeight: 600 }}>Ämne:</span> {plan.subject}</div>
    </div>
    <pre
      style={{
        fontSize: 12, color: '#0f172a', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        fontFamily: 'inherit', background: 'white', border: '1px solid #e2e8f0',
        borderRadius: 4, padding: '8px 10px', marginTop: 6, marginBottom: 0,
      }}
    >
      {plan.body}
    </pre>
    <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>
      Texten är renderad från en godkänd mall och kan inte ändras här — avvisa om något är fel.
    </div>
  </div>
);

/** Is this resolved plan a journal entry (verifikat) — a date + balanced debit/credit rows? */
const isJournalEntry = (plan) => plan && Array.isArray(plan.entries) && plan.entries.length > 0;

const fmtSEK = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v) || v === 0) return '';
  return new Intl.NumberFormat('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
};

/** The verifikat a human signs off on — header + the actual debet/kredit rows + totals. */
const JournalEntryPreview = ({ plan }) => {
  const entries = Array.isArray(plan.entries) ? plan.entries : [];
  const totDebit = entries.reduce((s, e) => s + (Number(e.debit) || 0), 0);
  const totCredit = entries.reduce((s, e) => s + (Number(e.credit) || 0), 0);
  const unbalanced = Math.abs(totDebit - totCredit) > 0.01;
  const cell = { padding: '4px 6px' };
  const num = { ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
  return (
    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '10px 12px', marginTop: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 6 }}>
        <FileText size={12} /> VERIFIKAT SOM BOKFÖRS
      </div>
      <div style={{ fontSize: 12, color: '#334155', marginBottom: 6 }}>
        {plan.date && <span style={{ marginRight: 12 }}><span style={{ fontWeight: 600 }}>Datum:</span> {plan.date}</span>}
        {plan.series && <span><span style={{ fontWeight: 600 }}>Serie:</span> {plan.series}</span>}
        {plan.description && <div style={{ marginTop: 2 }}><span style={{ fontWeight: 600 }}>Text:</span> {plan.description}</div>}
      </div>
      <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse', color: '#0f172a' }}>
        <thead>
          <tr style={{ color: '#64748b', textAlign: 'left' }}>
            <th style={cell}>Konto</th><th style={cell}>Beskrivning</th>
            <th style={{ ...cell, textAlign: 'right' }}>Debet</th><th style={{ ...cell, textAlign: 'right' }}>Kredit</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e, i) => (
            <tr key={i} style={{ borderTop: '1px solid #e2e8f0' }}>
              <td style={{ ...cell, fontWeight: 600 }}>{e.accountNumber ?? e.account ?? ''}</td>
              <td style={cell}>{e.description || ''}</td>
              <td style={num}>{fmtSEK(e.debit)}</td>
              <td style={num}>{fmtSEK(e.credit)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '2px solid #cbd5e1', fontWeight: 700 }}>
            <td style={cell} colSpan={2}><span style={{ float: 'right' }}>Summa</span></td>
            <td style={num}>{fmtSEK(totDebit)}</td>
            <td style={num}>{fmtSEK(totCredit)}</td>
          </tr>
        </tfoot>
      </table>
      {unbalanced && <div style={{ fontSize: 11, color: '#991b1b', marginTop: 4 }}>⚠ Debet och kredit balanserar inte.</div>}
    </div>
  );
};

/** Read-only details of the exact staged plan. */
const PlanDetails = ({ plan }) => {
  const entries = Object.entries(plan).filter(([, v]) => ['string', 'number', 'boolean'].includes(typeof v));
  if (entries.length === 0) return null;
  return (
    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '10px 12px', marginTop: 8 }}>
      {entries.map(([key, value]) => (
        <div key={key} className="d-flex align-items-center" style={{ fontSize: 12, padding: '2px 0', gap: 8 }}>
          <span style={{ color: '#64748b', minWidth: 140, flexShrink: 0 }}>{key}</span>
          <span style={{ color: '#0f172a', fontWeight: 500, wordBreak: 'break-word' }}>{String(value)}</span>
        </div>
      ))}

    </div>
  );
};

/**
 * One immutable agent approval with Godkänn/Avvisa. Corrections require a new staged plan.
 */
const AgentApprovalItem = ({ item, companyId, onResolved }) => {
  const {busy,error,decide}=useAgentApprovalDecision({companyId,item,onResolved});
  const [open,setOpen]=useState(false);
  const plan=item.resolvedPlan && typeof item.resolvedPlan==='object'?item.resolvedPlan:null;
  const rendered=isRenderedMessage(plan);
  const reconciliation=plan?.contract==='bank-reconciliation/1';
  const status=item.status||'pending';
  // A plan the backend has blocked can be neither approved nor corrected here.
  const blocked=plan?.policy?.decision==='blocked';

  return (
    <div style={{ padding: '10px 0', borderTop: '1px solid #f1f5f9' }}>
      <div className="d-flex align-items-center gap-3">
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
            {TOOL_LABEL[item.tool] || item.tool}
            {item.authzTier === 'needs-owner' && (
              <span
                title="Kräver ägarrollen"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 3,
                  fontSize: 10, fontWeight: 600, color: '#92400e',
                  background: '#fef3c7', borderRadius: 999, padding: '2px 8px',
                }}
              >
                <ShieldAlert size={11} /> Ägare
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item.summary}
          </div>
          {status === 'failed' && item.failureReason && (
            <div style={{ fontSize: 12, color: '#991b1b', marginTop: 2 }}>{item.failureReason}</div>
          )}
          {status === 'pending' && blocked && (
            <div style={{ fontSize: 12, color: '#92400e', marginTop: 2 }}>
              Förslaget kan inte godkännas som det är.{' '}
              {item.subjectId
                ? <Link to={`/${companyId}/bankstatements?filter=all&line=${item.subjectId}`} style={{ textDecoration: 'none' }}>Hantera bankhändelsen manuellt</Link>
                : 'Hantera bankhändelsen manuellt.'}
            </div>
          )}
          {error && <div style={{ fontSize: 12, color: '#991b1b', marginTop: 2 }}>{error}</div>}
        </div>
        <div className="d-flex gap-2 align-items-center" style={{ flexShrink: 0 }}>
          {plan && (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setOpen((o) => !o)}
              style={{ fontSize: 12, padding: '4px 10px', display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />} Detaljer
            </button>
          )}
          {status === 'pending' && !blocked && (
            <>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                disabled={!!busy}
                onClick={() => decide('approve')}
                style={{ fontSize: 12, padding: '4px 14px' }}
              >
                {busy === 'approve' ? 'Genomför…' : 'Godkänn'}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                disabled={!!busy}
                onClick={() => decide('reject')}
                style={{ fontSize: 12, padding: '4px 14px' }}
              >
                {busy === 'reject' ? 'Avvisar…' : 'Avvisa'}
              </button>
            </>
          )}
          {status === 'processing' && (
            <span
              title="Agenten genomför beslutet i bakgrunden — uppdateras automatiskt"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                fontSize: 12, fontWeight: 600, color: '#92400e',
                background: '#fef3c7', borderRadius: 999, padding: '4px 12px',
              }}
            >
              <Clock size={13} /> Behandlas…
            </span>
          )}
          {status === 'failed' && (
            <span
              title="Åtgärden kunde inte genomföras vid godkännande"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                fontSize: 12, fontWeight: 600, color: '#991b1b',
                background: '#fef2f2', borderRadius: 999, padding: '4px 12px',
              }}
            >
              <AlertTriangle size={13} /> Misslyckades
            </span>
          )}
        </div>
      </div>
      {open && reconciliation && <div style={{ marginTop: 8 }}><ActionPreview plan={plan} /></div>}
      {open && !reconciliation && rendered && <RenderedMessagePreview plan={plan} />}
      {open && !reconciliation && !rendered && isJournalEntry(plan) && <JournalEntryPreview plan={plan} />}
      {open && !reconciliation && !rendered && !isJournalEntry(plan) && plan && <PlanDetails plan={plan} />}
    </div>
  );
};

/**
 * A question from Mats about one bank line. Same header line as an approval
 * (what it is, which bank line), then the answer field; no approve/reject,
 * because an answer adds information and approves nothing.
 */
const AgentQuestionItem = ({ item, companyId, onAnswered }) => (
  <div style={{ padding: '10px 0', borderTop: '1px solid #f1f5f9' }}>
    <div className="d-flex align-items-center gap-3">
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>Fråga från Mats</div>
        {item.summary && (
          <div style={{ fontSize: 12, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item.summary}
          </div>
        )}
      </div>
      <div className="d-flex gap-3 align-items-center" style={{ flexShrink: 0 }}>
        {item.subjectId && (
          <Link to={`/${companyId}/bankstatements?filter=all&line=${item.subjectId}`} style={{ fontSize: 12, textDecoration: 'none' }}>
            Visa bankhändelse
          </Link>
        )}
        <ReconciliationTranscriptLink companyId={companyId} workId={item.workId} label="Visa samtal" />
      </div>
    </div>
    <ReconciliationQuestion companyId={companyId} workId={item.workId} question={item.resolvedPlan} onAnswered={onAnswered} />
  </div>
);

/** Itemised card (not a Link — it owns interactive buttons). */
const ApprovalListGroup = ({ group }) => {
  const Icon = DOMAIN_ICON[group.domain] || Bot;
  const { refresh } = useTodos();
  const params = useParams();
  // CompanyRoute mounts under "/" — sniff companyId from the path like TodoContext does.
  const companyId = params.companyId || window.location.pathname.split('/')[1];

  // Async approve: a decided row sits in 'processing' while the worker resumes + commits.
  // Poll so it flips to resolved (leaves the list) / failed without a manual refresh.
  const anyProcessing = (group.items || []).some((it) => (it.status || 'pending') === 'processing');
  useEffect(() => {
    if (!anyProcessing) return undefined;
    const id = setInterval(() => { refresh(); }, 4000);
    return () => clearInterval(id);
  }, [anyProcessing, refresh]);

  return (
    <div
      style={{
        background: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        padding: '14px 16px',
      }}
    >
      <div className="d-flex align-items-center gap-3" style={{ marginBottom: 4 }}>
        <div
          className="d-flex align-items-center justify-content-center"
          style={{
            width: 40, height: 40, borderRadius: 8,
            backgroundColor: 'var(--rgba-primary-1)',
            color: 'var(--bs-primary)',
            flexShrink: 0,
          }}
        >
          <Icon size={18} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{group.title}</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>
            {group.count === 1 ? '1 åtgärd väntar på godkännande' : `${group.count} åtgärder väntar på godkännande`}
          </div>
        </div>
      </div>
      {(group.items || []).map((item) => (
        item.presentationKind === 'question'
          ? <AgentQuestionItem key={item.questionId} item={item} companyId={companyId} onAnswered={refresh} />
          : <AgentApprovalItem key={item.approvalId} item={item} companyId={companyId} onResolved={refresh} />
      ))}
    </div>
  );
};

const TodoGroup = ({ group }) => {
  const accent = SEVERITY_ACCENT[group.severity] || SEVERITY_ACCENT.info;
  const Icon = DOMAIN_ICON[group.domain] || AlertTriangle;

  if (group.kind === 'approval_list') {
    return <ApprovalListGroup group={group} />;
  }

  return (
    <Link
      to={group.actionHref}
      className="todo-group"
      style={{ textDecoration: 'none' }}
    >
      <div
        className="d-flex align-items-center gap-3"
        style={{
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: 8,
          padding: '14px 16px',
          transition: 'all 0.15s ease',
        }}
      >
        <div
          className="d-flex align-items-center justify-content-center"
          style={{
            width: 40, height: 40, borderRadius: 8,
            backgroundColor: 'var(--rgba-primary-1)',
            color: 'var(--bs-primary)',
            flexShrink: 0,
          }}
        >
          <Icon size={18} />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', marginBottom: 2 }}>
            {group.title}
          </div>
          {group.kind === 'count' && (
            <div style={{ fontSize: 12, color: '#64748b' }}>
              <span style={{ fontSize: 22, fontWeight: 700, color: accent, marginRight: 6 }}>
                {group.count}
              </span>
              {group.count === 1 ? 'uppgift' : 'uppgifter'}
            </div>
          )}
          {group.kind === 'date' && (
            <div style={{ fontSize: 12, color: '#64748b' }}>
              <span style={{ fontSize: 15, fontWeight: 600, color: accent }}>
                {fmtDate(group.dueDate)}
              </span>
              {typeof group.daysUntilDue === 'number' && (
                <span style={{ marginLeft: 8 }}>
                  {group.daysUntilDue === 0 ? 'i dag'
                   : group.daysUntilDue === 1 ? 'i morgon'
                   : group.daysUntilDue > 0 ? `om ${group.daysUntilDue} dagar`
                   : `${Math.abs(group.daysUntilDue)} dagar försenad`}
                </span>
              )}
            </div>
          )}
        </div>

        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 4,
            fontSize: 13, color: 'var(--bs-primary)', fontWeight: 500,
          }}
        >
          {group.actionLabel || 'Öppna'}
          <ChevronRight size={16} />
        </div>
      </div>
    </Link>
  );
};

export default TodoGroup;
